import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from "react";
import type { FilterTypeName, GridFilter, GridLoader, GridQuery, SortDirection } from "@fortex/api";
import { filterKey, isAbortError, readField } from "@fortex/api";
import { Icon } from "../icons";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { Input } from "./Input";
import { Skeleton } from "./Skeleton";

export type GridFilterKind = "text" | "number" | "date" | "select" | "boolean" | "none";

export type GridFilterOption = { value: string; label: string };

export type GridColumnFilter = {
  kind: GridFilterKind;
  operators?: FilterTypeName[];
  options?: GridFilterOption[];
  field?: string;
};

export type GridColumn<TRow> = {
  key: string;
  header: string;
  field?: string;
  sortable?: boolean;
  filter?: GridColumnFilter;
  align?: "start" | "end" | "centre";
  width?: string;
  minWidth?: number;
  maxWidth?: number;
  mono?: boolean;
  render?: (row: TRow) => ReactNode;
};

export type DataGridLabels = {
  search: string;
  filters: string;
  clearFilters: string;
  results: string;
  noResults: string;
  perPage: string;
  page: string;
  first: string;
  previous: string;
  next: string;
  last: string;
  selectAll: string;
  selectRow: string;
  selected: string;
  clearSelection: string;
  all: string;
  yes: string;
  no: string;
  loadFailed: string;
  sortBy: string;
  collapse: string;
  expand: string;
  items: string;
  goToPage: string;
  resizeColumn: string;
  operators: Record<FilterTypeName, string>;
};

export const hungarianGridLabels: DataGridLabels = {
  search: "Keresés minden mezőben",
  filters: "Szűrők",
  clearFilters: "Szűrők törlése",
  results: "{from}–{to} / {total} találat",
  noResults: "Nincs megjeleníthető adat",
  perPage: "{size} / oldal",
  page: "{page} / {pages}",
  first: "Első oldal",
  previous: "Előző oldal",
  next: "Következő oldal",
  last: "Utolsó oldal",
  selectAll: "Mind kijelölése",
  selectRow: "Sor kijelölése",
  selected: "{count} kijelölve",
  clearSelection: "Kijelölés törlése",
  all: "Mind",
  yes: "Igen",
  no: "Nem",
  loadFailed: "A lista betöltése nem sikerült",
  sortBy: "Rendezés",
  collapse: "Összecsukás",
  expand: "Kinyitás",
  items: "{count} elem",
  goToPage: "{page}. oldal",
  resizeColumn: "{column} oszlop szélessége",
  operators: { Contains: "tartalmaz", Equal: "=", NotEqual: "≠", GreaterThan: ">", GreaterThanOrEqual: "≥", LessThan: "<", LessThanOrEqual: "≤" }
};

export const englishGridLabels: DataGridLabels = {
  search: "Search in every field",
  filters: "Filters",
  clearFilters: "Clear filters",
  results: "{from}–{to} of {total}",
  noResults: "No rows to show",
  perPage: "{size} per page",
  page: "{page} / {pages}",
  first: "First page",
  previous: "Previous page",
  next: "Next page",
  last: "Last page",
  selectAll: "Select all",
  selectRow: "Select row",
  selected: "{count} selected",
  clearSelection: "Clear selection",
  all: "All",
  yes: "Yes",
  no: "No",
  loadFailed: "The list could not be loaded",
  sortBy: "Sort by",
  collapse: "Collapse",
  expand: "Expand",
  items: "{count} items",
  goToPage: "Page {page}",
  resizeColumn: "Width of column {column}",
  operators: { Contains: "contains", Equal: "=", NotEqual: "≠", GreaterThan: ">", GreaterThanOrEqual: "≥", LessThan: "<", LessThanOrEqual: "≤" }
};

export type GridGroupOf<TRow> = (row: TRow) => string[];

export type DataGridProps<TRow> = {
  columns: GridColumn<TRow>[];
  rowKey: (row: TRow) => string;
  load: GridLoader<TRow>;
  labels?: DataGridLabels;
  defaultSortField?: string;
  defaultSortDirection?: SortDirection;
  pageSizes?: number[];
  toolbar?: ReactNode;
  quickFields?: string[];
  externalFilters?: GridFilter[];
  reloadToken?: number;
  selectable?: boolean;
  selectedKeys?: ReadonlySet<string>;
  onSelectionChange?: (keys: Set<string>, rows: TRow[]) => void;
  activeKey?: string | null;
  revealKey?: string | null;
  locate?: ((key: string, query: GridQuery, signal: AbortSignal) => Promise<number | null>) | null;
  onRowClick?: ((row: TRow) => void) | null;
  groupBy?: GridGroupOf<TRow> | null;
  onRowsChange?: (rows: TRow[]) => void;
  dense?: boolean;
  className?: string;
  dataFxmap?: string;
  resizableColumns?: boolean;
  pageNumbers?: number;
  clearableSort?: boolean;
  defaultFiltersOpen?: boolean;
};

type FilterState = Record<string, { operator: FilterTypeName; value: string }>;

type GroupNode<TRow> = { id: string; label: string; depth: number; rows: TRow[]; children: GroupNode<TRow>[]; count: number };

const defaultOperators: Record<GridFilterKind, FilterTypeName[]> = {
  text: ["Contains", "Equal", "NotEqual"],
  number: ["Equal", "GreaterThanOrEqual", "LessThanOrEqual", "NotEqual"],
  date: ["Equal", "GreaterThanOrEqual", "LessThanOrEqual"],
  select: ["Equal", "NotEqual"],
  boolean: ["Equal"],
  none: []
};

export const filterDelayMs = 300;

export const defaultColumnWidth = 160;

export const minColumnWidth = 40;

export const columnResizeStep = 10;

const noFilters: GridFilter[] = [];

export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}

export function clampColumnWidth<TRow>(column: GridColumn<TRow>, width: number): number {
  return Math.min(column.maxWidth ?? Number.POSITIVE_INFINITY, Math.max(column.minWidth ?? minColumnWidth, Math.round(width)));
}

export function initialColumnWidth<TRow>(column: GridColumn<TRow>): number {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(column.width?.trim() ?? "");

  return clampColumnWidth(column, match === null ? (column.minWidth ?? defaultColumnWidth) : Number(match[1]));
}

export function pageWindow(page: number, pageCount: number, size: number): number[] {
  const count = Math.max(0, Math.min(size, pageCount));
  const start = Math.max(0, Math.min(page - Math.floor(count / 2), pageCount - count));

  return Array.from({ length: count }, (_, index) => start + index);
}

export function columnField<TRow>(column: GridColumn<TRow>): string {
  return column.filter?.field ?? column.field ?? column.key;
}

export function buildFilters<TRow>(columns: GridColumn<TRow>[], state: FilterState, quick: string, quickFields: string[] | undefined, external: GridFilter[]): GridFilter[] {
  const result: GridFilter[] = [];

  for (const column of columns) {
    if (!column.filter || column.filter.kind === "none") continue;

    const current = state[column.key];

    if (current === undefined || current.value.trim() === "") continue;

    result.push({ fieldName: columnField(column), type: current.operator, value: current.value.trim() });
  }

  result.push(...external);

  const term = quick.trim();

  if (term.length > 0) {
    const fields = quickFields ?? columns.filter(a => a.filter && a.filter.kind !== "none" && a.filter.kind !== "boolean").map(columnField);

    for (const fieldName of new Set(fields)) result.push({ fieldName, type: "Contains", value: term, isQuick: true });
  }

  return result;
}

export function groupRows<TRow>(rows: TRow[], groupOf: GridGroupOf<TRow>): GroupNode<TRow>[] {
  const roots: GroupNode<TRow>[] = [];
  const byId = new Map<string, GroupNode<TRow>>();

  for (const row of rows) {
    const path = groupOf(row);
    let list = roots;
    let id = "";
    let node: GroupNode<TRow> | null = null;

    for (const [depth, label] of path.entries()) {
      id = `${id}/${label}`;

      let found = byId.get(id);

      if (found === undefined) {
        found = { id, label, depth, rows: [], children: [], count: 0 };
        byId.set(id, found);
        list.push(found);
      }

      found.count++;
      node = found;
      list = found.children;
    }

    if (node === null) {
      let loose = byId.get("/");

      if (loose === undefined) {
        loose = { id: "/", label: "", depth: 0, rows: [], children: [], count: 0 };
        byId.set("/", loose);
        roots.push(loose);
      }

      loose.count++;
      loose.rows.push(row);
    } else node.rows.push(row);
  }

  return roots;
}

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("button, a, input, select, textarea, label") !== null;
}

export function DataGrid<TRow>({
  columns,
  rowKey,
  load,
  labels = hungarianGridLabels,
  defaultSortField = "Id",
  defaultSortDirection = "ASC",
  pageSizes = [15, 25, 50, 100],
  toolbar,
  quickFields,
  externalFilters = noFilters,
  reloadToken = 0,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  activeKey = null,
  revealKey = null,
  locate = null,
  onRowClick = null,
  groupBy = null,
  onRowsChange,
  dense = false,
  className,
  dataFxmap,
  resizableColumns = false,
  pageNumbers = 0,
  clearableSort = false,
  defaultFiltersOpen = false
}: DataGridProps<TRow>) {
  const [rows, setRows] = useState<TRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizes[0] ?? 15);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  const [filters, setFilters] = useState<FilterState>({});
  const [quick, setQuick] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(defaultFiltersOpen);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [applied, setApplied] = useState<GridFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [ownSelection, setOwnSelection] = useState<Set<string>>(() => new Set());
  const appliedKey = useRef("");
  const rowsRef = useRef(rows);
  const queryRef = useRef<GridQuery | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingReveal = useRef<string | null>(null);
  const resizing = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  const selection = selectedKeys ?? ownSelection;

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = buildFilters(columns, filters, quick, quickFields, externalFilters);
      const key = filterKey(next);

      if (key === appliedKey.current) return;

      appliedKey.current = key;
      setPage(0);
      setApplied(next);
    }, filterDelayMs);

    return () => clearTimeout(timer);
  }, [columns, filters, quick, quickFields, externalFilters]);

  const query = useMemo<GridQuery>(() => ({ skip: page * pageSize, take: pageSize, sortField, sortDirection, filters: applied, isAnd: true }), [page, pageSize, sortField, sortDirection, applied]);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    if (revealKey === null) return;

    pendingReveal.current = revealKey;

    const current = queryRef.current;

    if (locate === null || current === null || rowsRef.current.some(a => rowKey(a) === revealKey)) return;

    const controller = new AbortController();

    locate(revealKey, current, controller.signal)
      .then(position => {
        if (controller.signal.aborted || position === null || position < 0) return;

        setPage(Math.floor(position / current.take));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [revealKey, locate, rowKey]);

  useEffect(() => {
    const key = pendingReveal.current;

    if (key === null || !rows.some(a => rowKey(a) === key)) return;

    pendingReveal.current = null;

    const element = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-fxmap="grid-row"]') ?? []).find(a => a.dataset.key === key);

    if (element !== undefined && typeof element.scrollIntoView === "function") element.scrollIntoView({ block: "nearest" });
  }, [rows, revealKey, rowKey]);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    load(query, controller.signal)
      .then(result => {
        if (controller.signal.aborted) return;

        setRows(result.rows);
        setTotal(result.total);
        setLoading(false);
        onRowsChange?.(result.rows);
      })
      .catch(reason => {
        if (controller.signal.aborted || isAbortError(reason)) return;

        setError(reason instanceof Error && reason.message.length > 0 ? reason.message : labels.loadFailed);
        setRows([]);
        setTotal(0);
        setLoading(false);
      });

    return () => controller.abort();
  }, [query, load, reloadToken, labels.loadFailed, onRowsChange]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const filterColumns = columns.filter(a => a.filter && a.filter.kind !== "none");
  const activeFilterCount = filterColumns.filter(a => (filters[a.key]?.value ?? "").trim() !== "").length;
  const columnCount = columns.length + (selectable ? 1 : 0);

  const toggleSort = (column: GridColumn<TRow>) => {
    if (!column.sortable) return;

    const field = column.field ?? column.key;

    if (field === sortField && clearableSort && sortDirection === "DESC") {
      setSortField(defaultSortField);
      setSortDirection(defaultSortDirection);
    } else if (field === sortField) setSortDirection(current => (current === "ASC" ? "DESC" : "ASC"));
    else {
      setSortField(field);
      setSortDirection("ASC");
    }
  };

  const changeSelection = useCallback(
    (next: Set<string>) => {
      if (selectedKeys === undefined) setOwnSelection(next);

      onSelectionChange?.(next, rowsRef.current.filter(a => next.has(rowKey(a))));
    },
    [selectedKeys, onSelectionChange, rowKey]
  );

  const toggleRow = (key: string, on: boolean) => {
    const next = new Set(selection);

    if (on) next.add(key);
    else next.delete(key);

    changeSelection(next);
  };

  const widthOf = (column: GridColumn<TRow>): number => widths[column.key] ?? initialColumnWidth(column);

  const resizeTo = (column: GridColumn<TRow>, width: number) => setWidths(current => ({ ...current, [column.key]: clampColumnWidth(column, width) }));

  const startResize = (event: PointerEvent<HTMLSpanElement>, column: GridColumn<TRow>) => {
    event.preventDefault();
    event.stopPropagation();
    resizing.current = { key: column.key, startX: event.clientX, startWidth: widthOf(column) };

    if (typeof event.currentTarget.setPointerCapture === "function") event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveResize = (event: PointerEvent<HTMLSpanElement>, column: GridColumn<TRow>) => {
    const current = resizing.current;

    if (current === null || current.key !== column.key) return;

    resizeTo(column, current.startWidth + event.clientX - current.startX);
  };

  const endResize = (event: PointerEvent<HTMLSpanElement>) => {
    resizing.current = null;

    if (typeof event.currentTarget.hasPointerCapture === "function" && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const keyResize = (event: KeyboardEvent<HTMLSpanElement>, column: GridColumn<TRow>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    resizeTo(column, widthOf(column) + (event.key === "ArrowRight" ? columnResizeStep : -columnResizeStep));
  };

  const pageKeys = rows.map(rowKey);
  const allChecked = pageKeys.length > 0 && pageKeys.every(a => selection.has(a));
  const someChecked = !allChecked && pageKeys.some(a => selection.has(a));

  const toggleAll = () => {
    const next = new Set(selection);

    if (allChecked) for (const key of pageKeys) next.delete(key);
    else for (const key of pageKeys) next.add(key);

    changeSelection(next);
  };

  const setFilterValue = (column: GridColumn<TRow>, value: string) => {
    const operators = column.filter?.operators ?? defaultOperators[column.filter?.kind ?? "text"];

    setFilters(current => ({ ...current, [column.key]: { operator: current[column.key]?.operator ?? operators[0], value } }));
  };

  const setFilterOperator = (column: GridColumn<TRow>, operator: FilterTypeName) => setFilters(current => ({ ...current, [column.key]: { operator, value: current[column.key]?.value ?? "" } }));

  const cell = (column: GridColumn<TRow>, row: TRow): ReactNode => {
    if (column.render) return column.render(row);

    const value = readField(row, column.field ?? column.key);

    if (typeof value === "boolean") return value ? labels.yes : labels.no;

    return value === null || value === undefined ? "" : String(value);
  };

  const onClickRow = (event: MouseEvent<HTMLTableRowElement>, row: TRow) => {
    if (onRowClick === null || isInteractive(event.target)) return;

    onRowClick(row);
  };

  const renderRow = (row: TRow, depth: number): ReactNode => {
    const key = rowKey(row);
    const checked = selection.has(key);
    const active = activeKey !== null && activeKey === key;

    return (
      <tr
        key={key}
        className={["fxmap-grid-row", onRowClick !== null ? "fxmap-grid-row-clickable" : "", checked ? "fxmap-grid-row-checked" : "", active ? "fxmap-grid-row-active" : ""].filter(Boolean).join(" ")}
        aria-selected={active || undefined}
        data-fxmap="grid-row"
        data-key={key}
        onClick={event => onClickRow(event, row)}
      >
        {selectable ? (
          <td className="fxmap-grid-check-cell">
            <input type="checkbox" className="fxmap-row-check" checked={checked} aria-label={labels.selectRow} onChange={event => toggleRow(key, event.target.checked)} />
          </td>
        ) : null}
        {columns.map((column, index) => (
          <td key={column.key} className={["fxmap-grid-cell", `fxmap-table-align-${column.align ?? "start"}`, column.mono ? "fxmap-mono" : ""].filter(Boolean).join(" ")} style={index === 0 && depth > 0 ? { paddingLeft: `calc(var(--fxmap-space-4) + ${depth * 20}px)` } : undefined}>
            {cell(column, row)}
          </td>
        ))}
      </tr>
    );
  };

  const renderGroups = (groups: GroupNode<TRow>[]): ReactNode[] =>
    groups.flatMap(groupNode => {
      if (groupNode.label === "" && groupNode.depth === 0) return groupNode.rows.map(a => renderRow(a, 0));

      const collapsed = collapsedGroups.has(groupNode.id);
      const header = (
        <tr key={groupNode.id} className="fxmap-grid-group" data-fxmap="grid-group" aria-expanded={!collapsed}>
          <td colSpan={columnCount} style={{ paddingLeft: `calc(var(--fxmap-space-3) + ${groupNode.depth * 20}px)` }}>
            <span className="fxmap-grid-group-inner">
              <Button
                kind="icon"
                size="sm"
                icon={collapsed ? "chevronRight" : "chevronDown"}
                label={collapsed ? labels.expand : labels.collapse}
                onClick={() =>
                  setCollapsedGroups(current => {
                    const next = new Set(current);

                    if (next.has(groupNode.id)) next.delete(groupNode.id);
                    else next.add(groupNode.id);

                    return next;
                  })
                }
              />
              <Icon name="folder" className="fxmap-grid-group-icon" />
              <span className="fxmap-grid-group-label fxmap-truncate">{groupNode.label}</span>
              <span className="fxmap-grid-group-count fxmap-muted fxmap-tabular">{fill(labels.items, { count: groupNode.count })}</span>
            </span>
          </td>
        </tr>
      );

      if (collapsed) return [header];

      return [header, ...renderGroups(groupNode.children), ...groupNode.rows.map(a => renderRow(a, groupNode.depth + 1))];
    });

  const body = groupBy === null ? rows.map(a => renderRow(a, 0)) : renderGroups(groupRows(rows, groupBy));
  const tableWidth = resizableColumns ? columns.reduce((sum, column) => sum + widthOf(column), selectable ? 40 : 0) : null;
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div ref={rootRef} className={["fxmap-grid", dense ? "fxmap-grid-dense" : "", className ?? ""].filter(Boolean).join(" ")} data-fxmap={dataFxmap ?? "data-grid"} aria-busy={loading || undefined}>
      <div className="fxmap-grid-toolbar">
        <div className="fxmap-grid-toolbar-left">
          {toolbar}
          {selectable && selection.size > 0 ? (
            <span className="fxmap-grid-selected fxmap-tabular" data-fxmap="grid-selected">
              {fill(labels.selected, { count: selection.size })}
              <Button kind="text" size="sm" icon="close" label={labels.clearSelection} onClick={() => changeSelection(new Set())} />
            </span>
          ) : null}
        </div>
        <div className="fxmap-grid-toolbar-right">
          <Input type="search" icon="search" value={quick} placeholder={labels.search} aria-label={labels.search} onChange={event => setQuick(event.target.value)} onClear={() => setQuick("")} clearLabel={labels.clearFilters} className="fxmap-grid-search" data-fxmap="grid-search" />
          {filterColumns.length > 0 ? <Button kind={filtersOpen || activeFilterCount > 0 ? "primary" : "ghost"} size="sm" icon="filter" label={activeFilterCount > 0 ? `${labels.filters} (${activeFilterCount})` : labels.filters} pressed={filtersOpen} onClick={() => setFiltersOpen(open => !open)} data-fxmap="grid-filters-toggle" /> : null}
        </div>
      </div>
      {filtersOpen && filterColumns.length > 0 ? (
        <div className="fxmap-grid-filters" data-fxmap="grid-filters">
          {filterColumns.map(column => {
            const kind = column.filter?.kind ?? "text";
            const operators = column.filter?.operators ?? defaultOperators[kind];
            const state = filters[column.key];
            const options = kind === "boolean" ? [{ value: "true", label: labels.yes }, { value: "false", label: labels.no }] : (column.filter?.options ?? []);

            return (
              <div className="fxmap-grid-filter" key={column.key}>
                <span className="fxmap-field-label">{column.header}</span>
                <div className="fxmap-grid-filter-row">
                  {operators.length > 1 ? (
                    <select className="fxmap-select fxmap-grid-filter-operator" value={state?.operator ?? operators[0]} aria-label={`${column.header} ${labels.sortBy}`} onChange={event => setFilterOperator(column, event.target.value as FilterTypeName)}>
                      {operators.map(operator => (
                        <option key={operator} value={operator}>
                          {labels.operators[operator]}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {kind === "select" || kind === "boolean" ? (
                    <select className="fxmap-select fxmap-grid-filter-value" value={state?.value ?? ""} aria-label={column.header} onChange={event => setFilterValue(column, event.target.value)}>
                      <option value="">{labels.all}</option>
                      {options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input className="fxmap-grid-filter-value" type={kind === "number" ? "number" : kind === "date" ? "date" : "text"} value={state?.value ?? ""} aria-label={column.header} onChange={event => setFilterValue(column, event.target.value)} />
                  )}
                </div>
              </div>
            );
          })}
          <div className="fxmap-grid-filters-clear">
            <Button kind="ghost" size="sm" icon="close" label={labels.clearFilters} disabled={activeFilterCount === 0} onClick={() => setFilters({})} />
          </div>
        </div>
      ) : null}
      {error !== null ? (
        <p className="fxmap-grid-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="fxmap-grid-scroll">
        <table className={["fxmap-grid-table", resizableColumns ? "fxmap-grid-table-resizable" : ""].filter(Boolean).join(" ")} style={tableWidth === null ? undefined : { width: `${tableWidth}px` }}>
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className="fxmap-grid-check-cell">
                  <input
                    type="checkbox"
                    className="fxmap-row-check"
                    checked={allChecked}
                    ref={element => {
                      if (element !== null) element.indeterminate = someChecked;
                    }}
                    aria-label={labels.selectAll}
                    onChange={toggleAll}
                  />
                </th>
              ) : null}
              {columns.map(column => {
                const field = column.field ?? column.key;
                const active = column.sortable === true && field === sortField;

                return (
                  <th key={column.key} scope="col" style={{ width: resizableColumns ? `${widthOf(column)}px` : column.width }} className={`fxmap-table-align-${column.align ?? "start"}`} aria-sort={active ? (sortDirection === "ASC" ? "ascending" : "descending") : column.sortable ? "none" : undefined}>
                    {column.sortable ? (
                      <button type="button" className="fxmap-table-sort" onClick={() => toggleSort(column)} title={labels.sortBy}>
                        <span>{column.header}</span>
                        <Icon name={active ? (sortDirection === "ASC" ? "sortAsc" : "sortDesc") : "sort"} className="fxmap-table-sort-icon" />
                      </button>
                    ) : (
                      column.header
                    )}
                    {resizableColumns ? (
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={fill(labels.resizeColumn, { column: column.header })}
                        aria-valuenow={widthOf(column)}
                        aria-valuemin={column.minWidth ?? minColumnWidth}
                        aria-valuemax={column.maxWidth}
                        tabIndex={0}
                        className="fxmap-grid-resize"
                        data-fxmap="grid-resize"
                        onPointerDown={event => startResize(event, column)}
                        onPointerMove={event => moveResize(event, column)}
                        onPointerUp={endResize}
                        onPointerCancel={endResize}
                        onKeyDown={event => keyResize(event, column)}
                        onClick={event => event.stopPropagation()}
                      />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columnCount}>
                  <Skeleton lines={4} />
                </td>
              </tr>
            ) : (
              body
            )}
          </tbody>
        </table>
        {!loading && rows.length === 0 && error === null ? <EmptyState icon="filter" title={labels.noResults} /> : null}
      </div>
      <div className="fxmap-grid-footer" role="navigation">
        <span className="fxmap-grid-results fxmap-tabular fxmap-muted" aria-live="polite">
          {fill(labels.results, { from, to, total })}
        </span>
        <span className="fxmap-grid-pager">
          <select
            className="fxmap-select"
            value={pageSize}
            aria-label={fill(labels.perPage, { size: pageSize })}
            onChange={event => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
          >
            {pageSizes.map(size => (
              <option key={size} value={size}>
                {fill(labels.perPage, { size })}
              </option>
            ))}
          </select>
          <Button kind="icon" size="sm" icon="pageFirst" label={labels.first} disabled={page === 0} onClick={() => setPage(0)} />
          <Button kind="icon" size="sm" icon="chevronLeft" label={labels.previous} disabled={page === 0} onClick={() => setPage(page - 1)} />
          <span className="fxmap-grid-page fxmap-tabular">{fill(labels.page, { page: page + 1, pages: pageCount })}</span>
          {pageNumbers > 0
            ? pageWindow(page, pageCount, pageNumbers).map(number => (
                <Button key={number} kind={number === page ? "primary" : "ghost"} size="sm" label={String(number + 1)} aria-label={fill(labels.goToPage, { page: number + 1 })} aria-current={number === page ? "page" : undefined} className="fxmap-grid-page-number" data-fxmap="grid-page-number" onClick={() => setPage(number)} />
              ))
            : null}
          <Button kind="icon" size="sm" icon="chevronRight" label={labels.next} disabled={page + 1 >= pageCount} onClick={() => setPage(page + 1)} />
          <Button kind="icon" size="sm" icon="pageLast" label={labels.last} disabled={page + 1 >= pageCount} onClick={() => setPage(pageCount - 1)} />
        </span>
      </div>
    </div>
  );
}
